export interface ScenarioConfig {
  id: string;
  name: string;
  description: string;
  positiveMean: number;
  positiveStd: number;
  negativeMean: number;
  negativeStd: number;
  prevalence: number;
  sampleSize: number;
  defaultThreshold: number;
}

export interface ClassifierMetrics {
  tp: number;
  fp: number;
  tn: number;
  fn: number;
  accuracy: number;
  precision: number;
  recall: number;
  f1: number;
  fpr: number;
  tpr: number;
}

export interface ROCPoint {
  fpr: number;
  tpr: number;
  threshold: number;
}

export interface PRPoint {
  recall: number;
  precision: number;
  threshold: number;
}

export const SCENARIOS: ScenarioConfig[] = [
  {
    id: 'ssh-brute-force',
    name: 'SSH Brute Force',
    description: 'Well-separated distributions, high prevalence. Easy scenario.',
    positiveMean: 0.75,
    positiveStd: 0.1,
    negativeMean: 0.3,
    negativeStd: 0.12,
    prevalence: 0.2,
    sampleSize: 1000,
    defaultThreshold: 0.5,
  },
  {
    id: 'phishing-detection',
    name: 'Phishing Detection',
    description: 'Moderate overlap, medium prevalence. Medium difficulty.',
    positiveMean: 0.65,
    positiveStd: 0.15,
    negativeMean: 0.35,
    negativeStd: 0.15,
    prevalence: 0.08,
    sampleSize: 1000,
    defaultThreshold: 0.5,
  },
  {
    id: 'insider-threat',
    name: 'Insider Threat',
    description: 'High overlap, very low prevalence. Hard scenario.',
    positiveMean: 0.58,
    positiveStd: 0.18,
    negativeMean: 0.42,
    negativeStd: 0.16,
    prevalence: 0.01,
    sampleSize: 2000,
    defaultThreshold: 0.5,
  },
  {
    id: 'malware-family',
    name: 'Malware Family',
    description: 'Moderate overlap, medium prevalence. Medium-hard.',
    positiveMean: 0.62,
    positiveStd: 0.14,
    negativeMean: 0.38,
    negativeStd: 0.14,
    prevalence: 0.05,
    sampleSize: 1000,
    defaultThreshold: 0.5,
  },
];

// Seeded pseudo-random number generator (xoshiro128**)
function createRng(seed: number): () => number {
  let s0 = seed | 0 || 1;
  let s1 = (seed * 2654435761) | 0 || 1;
  let s2 = (seed * 2246822519) | 0 || 1;
  let s3 = (seed * 3266489917) | 0 || 1;
  return () => {
    const result = (((s1 * 5) << 7) * 9) >>> 0;
    const t = s1 << 9;
    s2 ^= s0;
    s3 ^= s1;
    s1 ^= s2;
    s0 ^= s3;
    s2 ^= t;
    s3 = ((s3 << 11) | (s3 >>> 21)) >>> 0;
    return result / 4294967296;
  };
}

// Box-Muller transform for normally distributed values
function normalRandom(rng: () => number, mean: number, std: number): number {
  const u1 = rng();
  const u2 = rng();
  const z = Math.sqrt(-2 * Math.log(u1 || 1e-10)) * Math.cos(2 * Math.PI * u2);
  return Math.max(0, Math.min(1, mean + z * std));
}

export function generateScores(
  config: ScenarioConfig,
  seed = 42
): { positives: number[]; negatives: number[] } {
  const rng = createRng(seed);
  const numPositives = Math.round(config.sampleSize * config.prevalence);
  const numNegatives = config.sampleSize - numPositives;

  const positives: number[] = [];
  for (let i = 0; i < numPositives; i++) {
    positives.push(normalRandom(rng, config.positiveMean, config.positiveStd));
  }

  const negatives: number[] = [];
  for (let i = 0; i < numNegatives; i++) {
    negatives.push(normalRandom(rng, config.negativeMean, config.negativeStd));
  }

  return { positives, negatives };
}

export function computeMetrics(
  positives: number[],
  negatives: number[],
  threshold: number
): ClassifierMetrics {
  let tp = 0;
  let fn = 0;
  for (const score of positives) {
    if (score >= threshold) tp++;
    else fn++;
  }

  let fp = 0;
  let tn = 0;
  for (const score of negatives) {
    if (score >= threshold) fp++;
    else tn++;
  }

  const total = tp + fp + tn + fn;
  const accuracy = total > 0 ? (tp + tn) / total : 0;
  const precision = tp + fp > 0 ? tp / (tp + fp) : 0;
  const recall = tp + fn > 0 ? tp / (tp + fn) : 0;
  const f1 = precision + recall > 0 ? (2 * precision * recall) / (precision + recall) : 0;
  const fpr = fp + tn > 0 ? fp / (fp + tn) : 0;
  const tpr = recall;

  return { tp, fp, tn, fn, accuracy, precision, recall, f1, fpr, tpr };
}

export function computeROCPoints(
  positives: number[],
  negatives: number[],
  steps = 200
): ROCPoint[] {
  const points: ROCPoint[] = [];
  for (let i = 0; i <= steps; i++) {
    const threshold = i / steps;
    const m = computeMetrics(positives, negatives, threshold);
    points.push({ fpr: m.fpr, tpr: m.tpr, threshold });
  }
  return points;
}

export function computePRPoints(
  positives: number[],
  negatives: number[],
  steps = 200
): PRPoint[] {
  const points: PRPoint[] = [];
  for (let i = 0; i <= steps; i++) {
    const threshold = i / steps;
    const m = computeMetrics(positives, negatives, threshold);
    points.push({ recall: m.recall, precision: m.precision, threshold });
  }
  return points;
}

export function computeAUC(points: { x: number; y: number }[]): number {
  const sorted = [...points].sort((a, b) => a.x - b.x);
  let area = 0;
  for (let i = 1; i < sorted.length; i++) {
    const dx = sorted[i].x - sorted[i - 1].x;
    const avgY = (sorted[i].y + sorted[i - 1].y) / 2;
    area += dx * avgY;
  }
  return Math.abs(area);
}

export type OptimizeStrategy = 'balanced' | 'precision' | 'recall';

export function findOptimalThreshold(
  positives: number[],
  negatives: number[],
  strategy: OptimizeStrategy
): number {
  const steps = 500;
  let bestThreshold = 0.5;

  if (strategy === 'balanced') {
    let bestF1 = -1;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const m = computeMetrics(positives, negatives, t);
      if (m.f1 > bestF1) {
        bestF1 = m.f1;
        bestThreshold = t;
      }
    }
  } else if (strategy === 'precision') {
    // Find lowest threshold where precision >= 0.95
    bestThreshold = 1;
    for (let i = steps; i >= 0; i--) {
      const t = i / steps;
      const m = computeMetrics(positives, negatives, t);
      if (m.precision >= 0.95 && m.recall > 0) {
        bestThreshold = t;
      }
    }
  } else if (strategy === 'recall') {
    // Find highest threshold where recall >= 0.95
    bestThreshold = 0;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const m = computeMetrics(positives, negatives, t);
      if (m.recall >= 0.95) {
        bestThreshold = t;
      }
    }
  }

  return bestThreshold;
}
